import axios from "axios";
import { Listing, Service } from "../types";
import serialiseService from "./serialiseService";

export type SuccessfulSearchResult = {
  _status: {
    errors?: [];
  };
  _embedded: {
    healthcareServices: Array<Service>;
  };
  count: number;
  offset: number;
  limit: number;
};

export type NoServicesFound = {
  _status: {
    errors: Array<{
      type: string;
      code: string;
      message: string;
      developerMessage: string;
    }>;
  };
};

export type Pagination = {
  count: number;
  offset: number;
  limit: number;
};

export type SearchResult = SuccessfulSearchResult | NoServicesFound;

type ParamKeys =
  | "latitude"
  | "longitude"
  | "name"
  | "postcode"
  | "offset"
  | "language";
export type SearchParams = Partial<Record<ParamKeys, string>>;

function serialiseResponse(
  response: SuccessfulSearchResult["_embedded"]["healthcareServices"],
) {
  return response
    .map(serialiseService)
    .filter((listing) => !!listing.displayName);
}

const proximityParams = (lat: string, long: string) => {
  return {
    "location.proximity.near": `${lat},${long}`,
    "location.proximity.near_distance": "500000",
  };
};

const postcodeParams = (postcode: string) => {
  return {
    "location.proximity.near_postcode": postcode,
  };
};

const nameParams = (name: string) => {
  return {
    "search.name": name,
  };
};

const snomedParams = (params: SearchParams) => {
  const optometristSNOMEDcode = "310105000";
  const serviceType = `nhsd:/reference/taxonomies/snomed-servicetype/${optometristSNOMEDcode}`;

  return {
    "responseControl.offset": params.offset,
    "filter.serviceType.codes": serviceType,
    ...(params.language && params.language !== "en"
      ? {
          "practitioner.filter.language.codes": `nhsd:/reference/common/language/${params.language}`,
        }
      : {}),
  };
};

export const constructParams = (params: SearchParams) => {
  let combinedParams = snomedParams(params);

  const { latitude, longitude, name, postcode, offset } = params;

  if (latitude && longitude) {
    combinedParams = {
      ...combinedParams,
      ...proximityParams(latitude, longitude),
    };
  }

  if (name) {
    combinedParams = { ...combinedParams, ...nameParams(name) };
  }

  if (postcode) {
    combinedParams = { ...combinedParams, ...postcodeParams(postcode) };
  }

  return combinedParams;
};

const nhsdDirectorySearch = async (
  params: SearchParams,
): Promise<[Array<Listing>, Pagination | null]> => {
  const [response, pagination] = await axios
    .get<SearchResult>("/nhsd/search", {
      params: params,
    })
    .then((response) => {
      if ("_embedded" in response.data) {
        return [
          serialiseResponse(response.data._embedded.healthcareServices),
          {
            count: response.data.count,
            offset: response.data.offset,
            limit: response.data.limit,
          },
        ];
      }

      // TODO: Handle failure to find healthcare services
      // response.data._status.errors
      return [[], null];
    })
    .catch((error) => {
      // Not currently showing error messages
      console.warn(error);
      return [[], null];
    });

  return [response as Array<Listing>, pagination as Pagination];
};

export {
  nhsdDirectorySearch as default,
  proximityParams,
  snomedParams,
  nameParams,
  postcodeParams,
};
